//Components
import Avatar from '../../../../avatar/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Typography from '@mui/material/Typography'
import DataGrid from '../../../../table/DataGrid'
import Chip from '@mui/material/Chip'
import StatusChip from '../../components/StatusChip/StatusChip'
import IconTooltipButton from '../../../../button/IconTooltipButton'
import FilterCompletionButtom from './FilterCompletion/Button'

//Other dependencies
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { exportToCSV } from '../../../../../utils/file'
import './TableSelective.css'

const TableSelective = ({ filterx, disabled, onFilter, type, onPreview, dataYears = [] }) => {
  const tempList = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview?.users ?? [])
  const loading = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview?.loading ?? false)
  const skip = useSelector((state) => state?.datatable?.surveyPreviewList?.skip ?? 0)
  const rowsPerPage = useSelector((state) => state?.datatable?.surveyPreviewList?.rowsPerPage ?? 100)
  const columnsTable = useSelector((state) => state?.datatable?.surveyPreviewList?.columnsTable ?? [])
  const permissions = useSelector((state) => state?.session?.permissions?.dataentrysurveys ?? {})
  const [list, setList] = useState(tempList)
  const filter = useSelector((state) => state?.filter?.surveyPreviewList?.filter || {})
  const surveyData = useSelector((state) => state.dataEntrySurveyPreview?.surveyPreview ?? {})
  const { enqueueSnackbar } = useSnackbar()

  const getFilteredUsers = (users = [], filter) => {
    let newList = users

    if (filter?.code) {
      newList = newList.filter((item) => item.code?.toLowerCase().includes(filter.code.toLowerCase()))
    }
    if (filter?.name) {
      newList = newList.filter(
        (item) =>
          item?.firstName?.toLowerCase().includes(filter.name.toLowerCase()) ||
          item?.preferredName?.toLowerCase().includes(filter.name.toLowerCase()) ||
          item?.lastName?.toLowerCase().includes(filter.name.toLowerCase())
      )
    }
    if (filter?.status) {
      if (filter.status === 'completed') {
        newList = newList.filter((item) => item.isCompleted)
      }
      if (filter.status === 'pending') {
        newList = newList.filter((item) => !item.isCompleted)
      }
    }

    if (filter?.yearLevel?.length > 0) {
      const filterNames = filter?.yearLevel?.map((yl) => yl?.name)
      newList = newList.filter((item) => item.yearLevel?.some((yl) => filterNames?.includes(yl.name)))
    }

    return newList
  }

  const handleExportStudents = () => {
    try {
      // Get the full filtered list without pagination
      const filteredUsers = getFilteredUsers(tempList, filter)

      if (filteredUsers.length === 0) {
        enqueueSnackbar('No users found to export', { variant: 'warning' })
        return
      }

      // Map data for export
      const exportData = filteredUsers.map((student) => ({
        studentId: student?.code || student._id,
        firstName: student?.firstName || '',
        lastName: student?.lastName || '',
        yearLevel: student?.yearLevel?.map((yl) => yl.name).join(', ') || '',
        statusSurvey: student?.isCompleted ? 'Completed' : 'Pending',
        role: student?.roles?.map((role) => role.name).join(', ') || '',
        from: student?.userData?.data?.form || '',
        house: student?.userData?.data?.house || '',
        flag: student?.userData?.data?.flag?.join(', ') || ''
      }))

      // Set headers
      const headers = [
        { label: 'User ID', key: 'studentId' },
        { label: 'First Name', key: 'firstName' },
        { label: 'Last Name', key: 'lastName' },
        { label: 'Year Level', key: 'yearLevel' },
        { label: 'Survey Status', key: 'statusSurvey' },
        { label: 'Role', key: 'role' },
        { label: 'Form', key: 'from' },
        { label: 'House', key: 'house' },
        { label: 'Flag', key: 'flag' }
      ]

      // Generate filename
      const surveyName = surveyData?.name || 'survey'
      const timestamp = new Date().toISOString().split('T')[0]
      const filename = `${surveyName} - Users Report ${timestamp}`

      exportToCSV(exportData, headers, filename)
    } catch (error) {
      enqueueSnackbar('Error exporting users', { variant: 'error' })
    }
  }

  const filteUsers = (users = [], skipValue = 0, rowsPerPageValue = 50, filter) => {
    let newList = getFilteredUsers(users, filter)

    if (
      !Array.isArray(newList) ||
      !Number.isInteger(skipValue) ||
      !Number.isInteger(rowsPerPageValue) ||
      skipValue < 0 ||
      rowsPerPageValue <= 0
    ) {
      return newList
    }

    const startIndex = skipValue * rowsPerPageValue
    const endIndex = startIndex + rowsPerPageValue

    return newList.slice(startIndex, Math.min(endIndex, newList?.length))
  }

  useEffect(() => {
    setList(filteUsers(tempList, skip, rowsPerPage, filter))
  }, [tempList, skip, rowsPerPage, filter])

  return (
    <>
      <DataGrid
        id='survey-preview-table'
        actionsSetting={
          <Box className='flex items-center gap-2'>
            <FilterCompletionButtom
              disabled={disabled}
              filter={filter}
              onFilter={onFilter}
              dataYears={dataYears}
            />
            {permissions['list'] && tempList.length > 0 && (
              <Box className='flex items-center gap-2' sx={{ minWidth: '180px' }}>
                <Typography variant='body2' className='text-gray-600 ml-2'>
                  {getFilteredUsers(tempList, filter)?.length} users found
                </Typography>

                <Button
                  variant='outlined'
                  size='small'
                  disabled={disabled || loading}
                  onClick={handleExportStudents}
                  startIcon={<FontAwesomeIcon icon={['fal', 'download']} />}
                  className='export-button'
                  title='Export Users List'
                >
                  Export Summary
                </Button>
              </Box>
            )}
          </Box>
        }
        rows={list.map((row, index) => ({
          ...row,
          index: index + 1 + rowsPerPage * skip,
          avatar: <Avatar size='small' variant='' alt={row?.code} src={`photos/${row?.photo}`} />,
          code: row.code ?? '',
          name: `${row?.preferredName || row?.firstName} ${row?.lastName}`,
          roles: (
            <div className='flex flex-wrap gap-1'>
              {row?.roles?.map((role) => (
                <Chip key={`user-${row._id}-role-${role._id}`} size='small' label={role.name} />
              ))}
            </div>
          ),
          rolesSort: row?.roles?.map((role) => role?.name).join(', '),
          yearLevelUser: (
            <div className='flex flex-wrap items-center gap-1'>
              {row?.yearLevel?.map((yearLevel) => (
                <Chip
                  key={`user-${row._id}-yearLevel-${yearLevel._id}`}
                  size='small'
                  label={yearLevel.name}
                  variant='outlined'
                />
              ))}
            </div>
          ),
          yearLevelUserSort: row?.yearLevel?.map((yearLevel) => yearLevel?.name).join(', '),
          status: (
            <StatusChip
              content={row?.isCompleted ? 'Completed' : 'Pending'}
              type={row?.isCompleted ? 'done' : 'active'}
            />
          ),
          statusSort: row?.isCompleted ? 'Completed' : 'Pending',
          actions: (
            <div className='flex items-center gap-1 items-center justify-center'>
              {permissions['list'] && (
                <IconTooltipButton
                  id={`preview-${row._id}`}
                  disabled={loading}
                  title='Preview'
                  icon='expand-alt'
                  onClick={() => onPreview(row)}
                />
              )}
            </div>
          )
        }))}
        loading={loading}
        labelNoRows="You don't have any users yet"
        nameModule='surveyPreviewList'
        headers={[...columnsTable]}
        totalRows={tempList.length ?? 0}
        skip={skip}
        rowsPerPage={rowsPerPage || 10}
        columns={[
          {
            label: '#',
            key: 'index',
            align: 'center',
            width: 10,
            show: true,
            hide: false
          },
          ...columnsTable,
          {
            label: 'Actions',
            key: 'actions',
            align: 'center',
            show: true,
            hide: false
          }
        ]}
      />
    </>
  )
}
export default TableSelective
